import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Textarea } from 'app/components/ui/textarea';
import { Button } from 'app/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'app/components/ui/card';
import { Footprints, CheckCircle, AlertTriangle, Save } from 'lucide-react';
import { useSettingsSlice } from '../slice';

interface FooterSettingsProps {
  form: UseFormReturn<any>;
}

// Helper function to create payload with is_public for all keys
const createFooterSettingsPayload = (formData: any) => {
  const payload: Record<string, { option_value: any; is_public: number }> = {};

  // Add is_public: 1 for all footer settings
  const settingsKeys = [
    'footer_copyright',
    'footer_text',
    'footer_social_facebook',
    'footer_social_twitter',
    'footer_social_instagram',
    'footer_social_linkedin',
    'footer_social_youtube',
    'footer_social_telegram',
    'footer_social_whatsapp',
    'footer_show_social',
    'footer_show_newsletter',
    'footer_newsletter_text',
  ];

  settingsKeys.forEach(key => {
    if (formData[key] !== undefined) {
      payload[key] = {
        option_value: formData[key],
        is_public: 1,
      };
    }
  });

  return payload;
};

export default function FooterSettings({ form }: FooterSettingsProps) {
  const [updateStatus, setUpdateStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle');

  const { useSaveSiteSettingsMutation } = useSettingsSlice();
  const [saveSiteSettings, { isLoading: isUpdating }] =
    useSaveSiteSettingsMutation();

  // Function to handle settings update with is_public for all keys
  const handleUpdateSettings = async () => {
    try {
      setUpdateStatus('idle');
      const formData = form.getValues();
      const payload = createFooterSettingsPayload(formData);

      console.log('Footer settings payload with is_public:', payload);

      // Use the Redux mutation to save settings
      const result = await saveSiteSettings(payload).unwrap();
      console.log('Footer settings updated successfully:', result);

      setUpdateStatus('success');
      return result;
    } catch (error) {
      console.error('Error updating footer settings:', error);
      setUpdateStatus('error');
      throw error;
    }
  };

  return (
    <>
      {/* Footer Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Footprints className="w-5 h-5" />
            Footer Content
          </CardTitle>
          <CardDescription>
            Configure your site footer content and links.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="footer_copyright">Copyright Text</Label>
            <Input
              id="footer_copyright"
              {...form.register('footer_copyright')}
              placeholder="© 2024 Your Company. All rights reserved."
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="footer_text">Footer Description</Label>
            <Textarea
              id="footer_text"
              {...form.register('footer_text')}
              placeholder="Brief description about your company"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Fixed Save Button - Bottom Right */}
      <div className="fixed bottom-14 right-6 z-50">
        <div className="flex flex-col items-end gap-3">
          {/* Status Feedback */}
          {updateStatus === 'success' && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Footer settings saved successfully!
                </span>
              </div>
            </div>
          )}

          {updateStatus === 'error' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Failed to save footer settings. Please try again.
                </span>
              </div>
            </div>
          )}

          {/* Save Button */}
          <Button
            onClick={handleUpdateSettings}
            className="shadow-lg hover:shadow-xl transition-all duration-200"
            variant="default"
            disabled={isUpdating}
            size="lg"
          >
            {isUpdating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );
}
